import { createContext, useContext, useEffect, useState, ReactNode } from "react";

export type PersonaId = "consultant" | "departing" | "onboarding" | "contractor" | "ceo";

export type PersonaTransition = {
  id: number;
  employeeName: string;
  replacementName?: string | null;
  status: string;
  phase: string;
  lastDay: string;
};

export type Persona = {
  id: PersonaId;
  name: string;
  role: string;
  organisation: string;
  initials: string;
  accent: string;
  homePath: string;
  description: string;
  /**
   * Resolves the most relevant transition for this persona from the full list.
   * Returns null if there's nothing meaningful to bind to (e.g. CEO).
   */
  pickTransition: (all: PersonaTransition[]) => PersonaTransition | null;
};

const CAPTURE_OR_STRUCTURE = (t: PersonaTransition) => t.phase === "Capture" || t.phase === "Structure";
const TRANSFER_OR_MEASURE  = (t: PersonaTransition) => t.phase === "Transfer" || t.phase === "Measure";

export const PERSONAS: Record<PersonaId, Persona> = {
  consultant: {
    id: "consultant",
    name: "Alex Morgan",
    role: "Senior Consultant",
    organisation: "Relay Practice",
    initials: "AM",
    accent: "bg-blue-600",
    homePath: "/dashboard",
    description: "Manages every transition end-to-end across all client accounts.",
    pickTransition: () => null,
  },
  departing: {
    id: "departing",
    name: "Sarah Chen",
    role: "Departing — Senior Backend Engineer",
    organisation: "Nexflow Ltd",
    initials: "SC",
    accent: "bg-amber-600",
    homePath: "/me",
    description: "Going through the handover process before her last day.",
    // Active transition where Sarah is the departing employee, in Capture/Structure
    pickTransition: (all) =>
      all.find((t) => t.employeeName === "Sarah Chen" && t.status === "active" && CAPTURE_OR_STRUCTURE(t)) ??
      all.find((t) => t.employeeName === "Sarah Chen" && t.status === "active") ??
      null,
  },
  onboarding: {
    id: "onboarding",
    name: "James Murphy",
    role: "Incoming — Senior Backend Engineer",
    organisation: "Nexflow Ltd",
    initials: "JM",
    accent: "bg-emerald-600",
    homePath: "/me",
    description: "Stepping into Sarah's role with a 12-week onboarding plan.",
    // Transition where James is the named replacement, ideally in Transfer/Measure
    pickTransition: (all) =>
      all.find((t) => t.replacementName === "James Murphy" && TRANSFER_OR_MEASURE(t)) ??
      all.find((t) => t.replacementName === "James Murphy") ??
      null,
  },
  contractor: {
    id: "contractor",
    name: "Jamie Pearce",
    role: "External Contractor",
    organisation: "Nexflow Ltd",
    initials: "JP",
    accent: "bg-violet-600",
    homePath: "/me",
    description: "Engaged to bridge the gap during the handover window.",
    // First active transition at Nexflow that's still being captured
    pickTransition: (all) =>
      all.find((t) => t.status === "active" && CAPTURE_OR_STRUCTURE(t)) ??
      all.find((t) => t.status === "active") ??
      null,
  },
  ceo: {
    id: "ceo",
    name: "Michael Hartley",
    role: "Chief Executive",
    organisation: "Nexflow Ltd",
    initials: "MH",
    accent: "bg-slate-800",
    homePath: "/me",
    description: "Watches the fleet — focused on continuity risk and ROI.",
    pickTransition: () => null,
  },
};

const STORAGE_KEY = "relay.persona";

type ProfileContextValue = {
  persona: Persona;
  setPersonaId: (id: PersonaId) => void;
};

const ProfileContext = createContext<ProfileContextValue | null>(null);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const [personaId, setPersonaIdState] = useState<PersonaId>(() => {
    if (typeof window === "undefined") return "consultant";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored && stored in PERSONAS ? (stored as PersonaId) : "consultant";
  });

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, personaId);
  }, [personaId]);

  return (
    <ProfileContext.Provider
      value={{
        persona: PERSONAS[personaId],
        setPersonaId: (id) => setPersonaIdState(id),
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error("useProfile must be used within ProfileProvider");
  return ctx;
}
